import { Slot } from '@radix-ui/react-slot';
import { Link, useNavigate } from '@tanstack/react-router';
import { ButtonHTMLAttributes } from 'react';

import { RoleBasedAccess } from '@/components/RoleBasedAccess';
import { GRONDSLAGEN } from '@/constants/grondslagen';
import { BedrijfsInformatie } from '@/modals/BedrijfInformatie';
import { useUserStore } from '@/stores/user';

import { DropdownMenu } from '../DropdownMenu/DropdownMenu';
import { Logo } from '../Logo/Logo';
import { ModalTrigger } from '../Modal';

export const Navbar = () => {
  const loggedIn = useUserStore((state) => state.isLoggedIn);

  return (
    <div className="bg-white">
      <nav
        className="container-default relative flex h-11 items-center justify-between py-3"
        id="main-nav"
        aria-label="Hoofdnavigatie"
      >
        <Link
          to="/"
          search={{ p: 1 }}
          className="flex hover:underline"
          aria-label="Ga naar homepagina"
        >
          <Logo />
          <span className="style-body ml-2 text-gray-900">
            Altijd Actuele Digitale Vergunningen
          </span>
        </Link>

        {loggedIn ? (
          <div className="flex h-full items-center gap-4">
            <RoleBasedAccess
              allowed={['Beheerder']}
              area={['landelijk', 'omgevingsdienst']}
            >
              <NavbarButton asChild>
                <Link
                  to="/beheer/standaardteksten/$grondslagId"
                  params={{ grondslagId: GRONDSLAGEN[0].id }}
                  search={{ p: 1 }}
                  activeOptions={{ exact: false }}
                >
                  Beheer
                </Link>
              </NavbarButton>
            </RoleBasedAccess>

            <RoleBasedAccess allowed={['Beheerder']} area={['omgevingsdienst']}>
              <ModalTrigger
                modalComponent={BedrijfsInformatie}
                onOpen={() => ({
                  title: 'Bedrijf toevoegen',
                })}
                asChild
              >
                <NavbarButton type="button">Bedrijf toevoegen</NavbarButton>
              </ModalTrigger>
            </RoleBasedAccess>

            <div className="relative">
              <UserDropdown />
            </div>
          </div>
        ) : null}
      </nav>
    </div>
  );
};

const UserDropdown = () => {
  const user = useUserStore((state) => state.user);
  const userRole = useUserStore((state) => state.userRole);
  const logout = useUserStore((state) => state.logout);
  const navigate = useNavigate();

  const getUserInitials = (username: string) => {
    return username.charAt(0).toUpperCase();
  };

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <button
          aria-label="Gebruikersmenu"
          className="flex items-center rounded-full hover:underline focus-visible:outline-none focus-visible:ring focus-visible:ring-blue-500"
        >
          <span className="style-body mr-2">{user?.Gebruikersnaam}</span>

          <span
            id="user-icon-initials"
            className="style-button-label flex size-5 items-center justify-center rounded-full bg-black text-center text-white"
          >
            {user && user.Gebruikersnaam && (
              <span>{getUserInitials(user.Gebruikersnaam)}</span>
            )}
          </span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content>
        <DropdownMenu.Button className="pointer-events-none" asChild>
          <p>Rol: {userRole?.join(', ')}</p>
        </DropdownMenu.Button>

        <DropdownMenu.Button
          onClick={() => logout(() => navigate({ to: '/', search: { p: 1 } }))}
          type="button"
        >
          Uitloggen
        </DropdownMenu.Button>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

function NavbarButton({
  asChild,
  ...props
}: { asChild?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const Component = (asChild ? Slot : 'button') as 'button';

  return (
    <Component
      className="style-body inline-flex border-transparent text-black hover:underline focus:outline-none focus-visible:ring"
      {...props}
    />
  );
}
